import { deleteToken, Messaging } from 'firebase/messaging';
import { clearSessionScopedState } from '../api/api';
import { logoutUser } from '../api/user.api';
import { useAuth } from '../store/auth';
import { hasFirebaseMessaging } from './appType';
import { messaging } from './firebase';
import { getDeviceFcmToken } from './localstorage-helper';

/**
 * Sign out of THIS device.
 *
 * The order matters. `POST /v1/users/logout` has to go out while the access
 * token is still in `localStorage`, because the request interceptor reads it
 * from there - clearing the store first would send the call unauthenticated,
 * get a 401, and drive the interceptor into its own forced-logout path with a
 * spurious "session expired" toast.
 *
 * It also has to carry this device's FCM token. Without it the backend drops
 * every token the user has, so signing out here would kill push on their phone
 * as well.
 *
 * Everything is best effort: a failed logout call must not leave someone stuck
 * on a screen they are trying to leave, so the local state is cleared either
 * way.
 */
export const signOut = async (place: string) => {
  const fcmToken = getDeviceFcmToken();

  try {
    await logoutUser(fcmToken);
  } catch {
    // The session is being abandoned regardless; a failed call only means the
    // backend prunes the row later, when Firebase rejects a push to it.
  }

  await forgetSession(place);
};

/**
 * Sign out after `DELETE /v1/users`: the account and every session it had are
 * already gone, so there is nothing to log out of - the call would only 401
 * into the interceptor's forced logout. Drops this device's state alone.
 */
export const signOutDeletedAccount = () => forgetSession('account deleted');

const forgetSession = async (place: string) => {
  // Stop this browser holding a subscription the server no longer knows about.
  // Only the apps with push (admin, partner, the vDrive console) initialise
  // messaging - elsewhere it is `false`.
  if (hasFirebaseMessaging && messaging) {
    try {
      await deleteToken(messaging as Messaging);
    } catch {
      /* Nothing to revoke, or the SW is already gone. */
    }
  }

  // Also drops the cached access token and, in the vDrive console, the
  // selected organization (see `clearSessionScopedState`).
  clearSessionScopedState();

  useAuth.setState({
    user: null,
    tokens: null,
    lastLogout: {
      date: new Date().toISOString(),
      status: 0,
      error: '',
      place,
    },
  });
};
